import React, { useState, useEffect } from 'react';
import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory } from '../utils/i18n';
import { 
  Truck, Navigation, Globe, Download, Copy, Check, 
  ArrowLeft, Sparkles, FileText, Upload, RefreshCw, FileCheck, X,
  ShieldCheck, AlertTriangle
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import { autoDownloadReport } from '../utils/autoDownloader';
import TelegramExportButton from './TelegramExportButton';
import ExcelExportButton from './ExcelExportButton';
import { DigitalSignatureModal } from './DigitalSignatureModal';
import { getFieldLabel, getFieldValue, getNotesText } from '../utils/demoPresetsI18n';

interface LogisticsAgentViewProps {
  agentId: string;
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

const DEFAULT_LOGISTICS_DEMO: Record<string, {
  field1LabelUk: string; field1Value: string;
  field2LabelUk: string; field2Value: string;
  field3LabelUk: string; field3Value: string;
  field4LabelUk: string; field4Value: string;
  field5LabelUk: string; field5Value: string;
  field6LabelUk: string; field6Value: string;
  notesText: string;
}> = {
  'customs-eur1-approved-exporter-builder': {
    field1LabelUk: "Найменування експортного товару & Код УКТ ЗЕД",
    field1Value: "Меблеві фасади з натурального дуба (Код УКТ ЗЕД 9403 91 00 00 / CN 9403 91 00) / 1 200 м²",
    field2LabelUk: "Країна призначення в ЄС & Угода (Pan-Euro-Med)",
    field2Value: "Німеччина (DE) / Регіональна конвенція Pan-Euro-Med (Угода про асоціацію Україна-ЄС)",
    field3LabelUk: "Калькуляція собівартості (Ціна Франко-завод ex-works)",
    field3Value: "Загальна вартість партії ex-works: 42 000.00 EUR (35.00 EUR/м²) / Собівартість виробництва: 32 500.00 EUR",
    field4LabelUk: "Вартість імпортної (непоходження) сировини",
    field4Value: "Імпортний лак та фурнітура (Китай): 4 200.00 EUR (10% від ціни ex-works, ліміт не більше 40%)",
    field5LabelUk: "Декларації постачальників сировини & Виробництво",
    field5Value: "Дубова дошка українського походження (Декларація постачальника № 14, лісгосп України), повний технологічний цикл розпилу, сушіння та фрезерування",
    field6LabelUk: "Бажаний статус (Сертифікат EUR.1 чи Уповноважений)",
    field6Value: "Оформлення Сертифіката EUR.1 + Пакет документів на отримання статусу Уповноваженого експортера (Approved Exporter)",
    notesText: "Провести розрахунок критерію достатньої переробки за правилами конвенції Pan-Euro-Med. Перевірити, що частка імпортної сировини (10%) не перевищує допустимий поріг 40% від ціни франко-завод (ex-works). Сформувати офіційну Заяву на видачу Сертифіката EUR.1, Розрахунок собівартості, Декларацію виробника та пакет на отримання статусу Уповноваженого експортера у Word (.docx) за ДСТУ 4163:2020."
  },
  'customs-aeo-compliance-auditor': {
    field1LabelUk: "Тип авторизації АЕО (АЕО-С / АЕО-Б / АЕО-Ф)",
    field1Value: "АЕО-С (Митні спрощення) + АЕО-Б (Безпека та надійність) — Повний статус АЕО-Ф",
    field2LabelUk: "Обсяги ЗЕД-діяльності (Кількість ВМД за 3 роки)",
    field2Value: "340 оформлених митних декларацій (імпорт/експорт) за останні 3 роки / Загальний фактурний оборот: 18.5 млн EUR",
    field3LabelUk: "Стан системи бухгалтерського & складського обліку",
    field3Value: "ERP система SAP / 1C:Підприємство з логуванням дій користувачів, WMS система адресного зберігання на митному ліцензійному складі",
    field4LabelUk: "Заходи фізичної безпеки та захисту ланцюга поставок",
    field4Value: "Відеоспостереження периметра 24/7 (архів 90 діб), СКУД із біометрією, опломбування вантажівок ліцензованими пломбами ISO 17712",
    field5LabelUk: "Фінансова платоспроможність (Баланс, Чисті активи)",
    field5Value: "Чисті активи: 45 000 000 грн (перевищують статутний капітал), відсутність податкового боргу та відкритих виконавчих проваджень",
    field6LabelUk: "Наявність кваліфікованого персоналу з митних питань",
    field6Value: "3 штатні кваліфіковані митні брокери з дійсними свідоцтвами ДМСУ та досвідом понад 5 років",
    notesText: "Провести комплексну оцінку готовності компанії до отримання авторизацій АЕО-С та АЕО-Б відповідно до ст. 12-15 МКУ та критеріїв UCC. Заповнити офіційний Опитувальник самооцінки (SAQ) за розділами: комплаєнс-історія, надійність обліку, фінансова стабільність, безпека ланцюга поставок. Сформувати Звіт прогалин (Gap Analysis) та план впровадження у Word (.docx) за ДСТУ 4163:2020."
  },
  'customs-dualuse-sanctions-counsel': {
    field1LabelUk: "Технічний опис товару & Параметри (Специфікація)",
    field1Value: "Промислова тепловізійна камера нічного бачення з роздільною здатністю 640x512 та частотою кадрів 60 Гц (Виробництво: Німеччина)",
    field2LabelUk: "Код УКТ ЗЕД & Орієнтовна категорія ПКМУ № 86",
    field2Value: "Код 9027 50 00 00 / Категорія Єдиного списку: 6A003.b.4 (Оптичні сенсори та камери спеціального призначення)",
    field3LabelUk: "Дані кінцевого споживача (End-User) & Країна",
    field3Value: "«AeroTech Engineering Sp. z o.o.» (Польща, м. Краків) / Завод з виробництва цивільних інспекційних дронів",
    field4LabelUk: "Сфера кінцевого використання продукції",
    field4Value: "Виключно цивільне використання: інтеграція в БПЛА для моніторингу високовольтних ліній електропередач та лісових пожеж",
    field5LabelUk: "Результати санкційного скринінгу (РНБО/OFAC/EU)",
    field5Value: "Контрагент та його бенефіціари відсутні у санкційних списках РНБО, OFAC SDN, EU Consolidated, UK Sanctions List",
    field6LabelUk: "Вид запитуваного документа ДСЕКУ",
    field6Value: "Висновок ДСЕКУ про неналежність товару до товарів військового призначення / Разовий дозвіл на імпорт/експорт товарів подвійного використання",
    notesText: "Здійснити техніко-юридичну ідентифікацію обладнання за критеріями ПКМУ № 86 та Регламенту ЄС 2021/821. Провести санкційний скринінг покупця та кінцевого користувача. Скласти Сертифікат кінцевого споживача (End-User Certificate EUC), гарантійні листи про нереекспорт та заяву до Державної служби експортного контролю України (ДСЕКУ) у Word (.docx) за ДСТУ 4163:2020."
  },
  'customs-inward-processing-ata-manager': {
    field1LabelUk: "Вид операції (Переробка на МТУ ІМ51 / Карнет АТА)",
    field1Value: "Митний режим переробки на митній території України (ІМ-51) з умовним повним звільненням від оподаткування",
    field2LabelUk: "Опис та вартість сировини/обладнання для ввезення",
    field2Value: "Тканина бавовняна та фурнітура (Італія) / 5 000 погонних метрів / Фактурна вартість: 35 000.00 EUR (DAP Львів)",
    field3LabelUk: "Технологічний процес переробки & Операції",
    field3Value: "Розкрій, пошиття робочого спецодягу, брендування, маркування та пакування на потужностях швейної фабрики у м. Львів",
    field4LabelUk: "Норми виходу готової продукції (Yield Ratio & Втрати)",
    field4Value: "Коефіцієнт виходу: 94.5% (2 100 комплектів спецодягу з 5 000 м тканини) / Технологічні відходи (обрізки): 5.5% (275 кг)",
    field5LabelUk: "Опис готової компенсаційної продукції (Код УКТ ЗЕД)",
    field5Value: "Костюми чоловічі виробничі (Код УКТ ЗЕД 6203 22 10 00) / 2 100 шт / Реекспорт у режимі ЕК-51 до Німеччини",
    field6LabelUk: "Строк переробки або перебування за Карнетом АТА",
    field6Value: "Запитуваний строк переробки: 180 календарних днів (до 15.02.2027) / Фінансова гарантія сплати митних платежів",
    notesText: "Скласти комплект документів на відкриття та супровід митного режиму переробки (ІМ51): (1) Заяву на отримання дозволу митниці на переробку товарів (ст. 149 МКУ); (2) Технологічну схему переробки та розрахунок норм виходу готової продукції; (3) Проєкт Звіту про завершення режиму переробки (ст. 150 МКУ); (4) Форми для Карнета АТА у Word (.docx) та Excel (.xlsx)."
  },
  'customs-post-clearance-485-auditor': {
    field1LabelUk: "Реквізити Акта перевірки / Наказу про перевірку",
    field1Value: "Акт документальної планової виїзної перевірки № 48/10-00/39481920 від 12.01.2026, складений Головним управлінням ДМСУ",
    field2LabelUk: "Період перевірки & Кількість охоплених ВМД",
    field2Value: "Період: 01.01.2023 – 31.12.2025 (3 роки / 1095 днів) / Перевірено 86 оформлених митних декларацій типу ІМ-40",
    field3LabelUk: "Суть претензій митниці (Фрахт, Роялті, Класифікація)",
    field3Value: "Митниця стверджує про неповне включення витрат на транспортування до кордону (фрахт) до митної вартості за умовами FCA та невключення ліцензійних платежів",
    field4LabelUk: "Сума донарахованих платежів & Штраф за ст. 485 МКУ",
    field4Value: "Донараховано митних платежів (мито + ПДВ): 840 000.00 грн + загроза штрафу за ст. 485 МКУ (300% або 100% суми)",
    field5LabelUk: "Наявні первинні докази та бухгалтерські регістри",
    field5Value: "Акти виконаних робіт перевізників з чітким розділенням фрахту «до кордону» та «після кордону України», ліцензійна угода без прив'язки до імпортованих товарів",
    field6LabelUk: "Стадія оскарження (Заперечення на Акт / Судовий позов)",
    field6Value: "Подання Заперечень на Акт перевірки за ч. 5 ст. 352 МКУ (строк 10 робочих днів) + Підготовка адміністративного позову про скасування ППР форми «М»",
    notesText: "Провести правовий аудит висновків Акта документальної перевірки митного органу. Спростувати нарахування митних зобов'язань щодо транспортних витрат та ліцензійних платежів за ст. 58 МКУ. Скласти офіційні Заперечення на Акт перевірки до ДМСУ та проєкт Позовної заяви до адміністративного суду про скасування податкових повідомлень-рішень форми «М» у Word (.docx) за ДСТУ 4163:2020."
  },
  'customs-full-pack-doc-generator': {
    field1LabelUk: "Реквізити сторін (Експортер & Імпортер / EORI)",
    field1Value: "Експортер: ТОВ «УкрПромЕкспорт» (код ЄДРПОУ 39481920, Україна, м. Київ) / Імпортер: «PolTech Sp. z o.o.» (NIP: 5252841920, EORI: PL52528419200000, Польща, м. Варшава)",
    field2LabelUk: "Номенклатура товарів & Коди УКТ ЗЕД / CN",
    field2Value: "Товар 1: Деталі машин для обробки металу (Код 8466 93 70 00, 150 шт, 450 EUR/шт) / Товар 2: Електродвигуни змінного струму (Код 8501 52 20 00, 50 шт, 620 EUR/шт)",
    field3LabelUk: "Умови поставки Incoterms 2020 & Пункт",
    field3Value: "FCA Київ (вул. Промислова, 14) згідно з Incoterms 2020 / Перехід ризиків: момент передачі перевізнику",
    field4LabelUk: "Вартість контракту, Інвойс & Валюта (USD/EUR)",
    field4Value: "Загальна фактурна вартість інвойсу: 98 500.00 EUR (Без ПДВ, експорт 0%) / Умови оплати: 100% передоплата за банківським переказом SWIFT",
    field5LabelUk: "Параметри пакування (Палети, Місця, Вага брутто/нетто)",
    field5Value: "Загальна вага нетто: 4 850.00 кг / Вага брутто: 5 240.00 кг / Кількість вантажних місць: 8 європалет (1200x800x1600 мм), об'єм 12.28 м³",
    field6LabelUk: "Арбітраж, Форс-мажор & Банківські реквізити (IBAN)",
    field6Value: "Арбітраж: МКАС при ТПП України / Застосовне право: Матеріальне право України / Форс-мажор: ТПП України / IBAN UA48305299000002600841920000",
    notesText: "Сформувати повний, готовий до митного оформлення та підписання пакет документів ЗЕД: (1) Двомовний ЗЕД-контракт купівлі-продажу (UA/EN) за правилами Incoterms 2020; (2) Комерційний інвойс (Commercial Invoice) з усіма митними графами та реквізитами сторін; (3) Пакувальний лист (Packing List) з деталізацією ваги, місць та габаритів; (4) Специфікацію товару (Додаток № 1). Експортувати весь пакет у Word (.docx) за ДСТУ 4163:2020 та розрахунковий Excel (.xlsx)."
  },
  'customs-sad-md2-builder-xml': {
    field1LabelUk: "Тип декларації & Митний режим (ІМ40 / ЕК10)",
    field1Value: "Електронна митна декларація МД-2 / SAD (Режим: ІМ-40 ДЕ — Імпорт у вільний обіг з попередньою декларацією)",
    field2LabelUk: "Митний орган оформлення (Код поста ДМСУ / ЄС)",
    field2Value: "UA100030 (Київська митниця, митний пост «Святошин») / Пункт пропуску на кордоні: UA209010 («Ягодин — Дорогуськ»)",
    field3LabelUk: "Опис товару, Код УКТ ЗЕД (10 знаків) & Кількість",
    field3Value: "Код УКТ ЗЕД: 8479 89 97 90 (Обладнання промислове автоматизоване) / 2 комплекти / Вага нетто: 6 400 кг, брутто: 6 950 кг",
    field4LabelUk: "Фактурна & Митна вартість (Графи 22, 42, 45)",
    field4Value: "Графа 22 (Фактурна вартість): 124 000.00 EUR (DAP Київ) / Фрахт включено / Графа 45 (Митна вартість у грн): 5 580 000.00 грн (курс НБУ 45.00)",
    field5LabelUk: "Коди преференцій (Графа 36) & EUR.1 / Сертифікат",
    field5Value: "Графа 36 (Код преференції): 400/000/00 (Угода про вільну торгівлю Україна–ЄС) / Сертифікат з перевезення EUR.1 № PL/2026/004921",
    field6LabelUk: "Розрахунок Графи 47 (Мито, Акциз, ПДВ, Збір)",
    field6Value: "Ввізне мито (код 020): 0% (преференція EUR.1 = 0 грн) / ПДВ 20% (код 028): 1 116 000.00 грн / Митний збір (код 010): 1 800.00 грн / Разом: 1 117 800.00 грн",
    notesText: "Здійснити повний розрахунок усіх 54 граф митної декларації МД-2 / SAD за правилами ПКМУ № 468. Розрахувати митну вартість, нарахувати платежі у Графі 47 (мито, акциз, ПДВ), сформувати структурований XML-файл для імпорту в MD Office / QD Pro та згенерувати готовий бланк декларації у Word (.docx) за ДСТУ 4163:2020."
  },
  'customs-value-appeal-auditor': {
    field1LabelUk: "Реквізити Рішення про коригування митної вартості",
    field1Value: "Рішення про коригування митної вартості товарів № 100000/2026/000482 від 10.02.2026, видане Київською митницею Держмитслужби",
    field2LabelUk: "Задекларована ціна (1 метод) ↔ Скоригована митницею",
    field2Value: "Задекларовано за 1-м методом (ціна контракту): 45 000.00 USD (1.80 USD/кг) ↔ Скориговано митницею за 6-м методом (резервним): 72 000.00 USD (2.88 USD/кг)",
    field3LabelUk: "Підстави коригування, зазначені митницею",
    field3Value: "Формальні сумніви щодо вартості, посилання на цінову базу АСМО «Інспектор» за аналогічними товарами, відсутність у контракті окремого прайс-листа виробника",
    field4LabelUk: "Надані первинні документи (ст. 53 МКУ / Інвойс, Прайс)",
    field4Value: "ЗЕД-контракт № 12, Комерційний інвойс № 48, Пакувальний лист, Експортна декларація країни відправлення (Польща, SAD 10EX), SWIFT-платіжка 100% передоплати, договір перевезення",
    field5LabelUk: "Сума фінансової гарантії (застави) & Переплачені мита",
    field5Value: "Внесено грошову заставу за фінансовою гарантією за ст. 55 МКУ: 275 400.00 грн (Різниця між задекларованими та скоригованими платежами)",
    field6LabelUk: "Орган оскарження (ДМСУ / Окружний адміністративний суд)",
    field6Value: "Київський окружний адміністративний суд (КОАС) + Скарга до центрального апарату Державної митної служби України (ст. 24–30 МКУ)",
    notesText: "Провести правовий аудит Рішення митниці про коригування митної вартості. Спростувати застосування другорядних методів (2-6) за ст. 57, 58, 64 МКУ та правовими позиціями Верховного Суду (щодо неприпустимості коригування лише на підставі цінових орієнтирів бази АСМО «Інспектор»). Скласти повний текст Адміністративного позову про визнання рішення протиправним, його скасування та зобов'язання повернути фінансову заставу у сумі 275 400.00 грн у Word (.docx) за ДСТУ 4163:2020."
  },
  'logistics-ncts-t1-transit-declarator': {
    field1LabelUk: "Номер транзитної декларації / Режим (T1 / T2 / NCTS)",
    field1Value: "Транзитна декларація T1 (NCTS Фаза 5) / MRN: 26PL322010T004819X / Вантажний автопоїзд 22т",
    field2LabelUk: "Митні органи відправлення & призначення",
    field2Value: "Митниця відправлення: UA100010 (Київська митниця) ↔ Митниця призначення: PL322010 (Oddział Celny w Gdyni)",
    field3LabelUk: "Коди УКТ ЗЕД / CN & Вартість вантажу (EUR)",
    field3Value: "CN 1005 90 00 (Кукурудза фуражна, 22.5 тонни) / Фактурна вартість: 48 200.00 EUR",
    field4LabelUk: "Вид фінансової гарантії (TC31 / TC32 / GRN)",
    field4Value: "Генеральна фінансова гарантія TC31 (GRN: 26PL000000GRN4812A) / Сума покриття боргу: 12 450 EUR",
    field5LabelUk: "Митні пломби & Ідентифікатори автотранспорту",
    field5Value: "Пломби типу Tyden / Bolt Seal № UA-CUSTOMS-84192 / Тягач: AA 4819 OO / Напівпричіп: AA 9210 XX",
    field6LabelUk: "Граничний термін доставки & Митний статус",
    field6Value: "Термін транзиту: 8 діб (до 23.08.2026) / Статус вантажовідправника: Авторизований вантажовідправник (спрощення AEO)",
    notesText: "Скласти та валідувати електронну транзитну декларацію T1 у структурі NCTS (Фаза 5). Розрахувати граничну суму митного боргу для списання з генеральної фінансової гарантії TC31, перевірити коректність маршруту слідування та кодів митних постів і сформувати офіційний транзитний супровідний документ (TAD) та файл IE015 XML у Word (.docx) за ДСТУ 4163:2020 та Excel (.xlsx)."
  },
  'customs-sad-md2-declarator': {
    field1LabelUk: "Номер декларації / Формат & Товарний опис",
    field1Value: "ВМД форма МД-2 / SAD (ІМ-40 ДЕ) / Лінія з фасування зернових культур у мішки",
    field2LabelUk: "Код УКТ ЗЕД (10 знаків) ↔ Код ЄС (CN / TARIC)",
    field2Value: "8422 30 00 00 (УКТ ЗЕД) ↔ 8422 30 00 90 (TARIC EU) — Ставка мита 0%, ПДВ 20%",
    field3LabelUk: "Країна походження & Митна вартість (CIF / DAP)",
    field3Value: "Польща (PL) / 64 500 EUR (DAP Київ) / Митна вартість: 2 890 000 грн",
    field4LabelUk: "Супровідні документи & Сертифікат EUR.1",
    field4Value: "Інвойс № PL-4819, Пакувальний лист, Сертифікат походження EUR.1 № PL/2026/004812 (преференція 0% мито)",
    field5LabelUk: "Тарифи & Розрахунок платежів (Мито, Акциз, ПДВ)",
    field5Value: "Ввізне мито 0% (преференція Угода про асоціацію), ПДВ 20%: 578 000 грн, Митне оформлення: 1 200 грн",
    field6LabelUk: "Технічні регламенти & Нетарифне регулювання",
    field6Value: "Декларація про відповідність ТР Безпеки машин (ПКМУ № 62) та ТР Електромагнітної сумісності (ПКМУ № 1077)",
    notesText: "Здійснити комплексну валідацію проекту митної декларації МД-2 / SAD. Звірити 10-значний код УКТ ЗЕД із товарною номенклатурою ЄС (TARIC). Перевірити повноту та коректність заявленої митної вартості за методом 1 (ціна контракту), застосування преференційного тарифу за сертифікатом EUR.1, відповідність інвойсів пакувальним листам та сформувати офіційний Чек-лист митного комплаєнсу у Word (.docx) за ДСТУ 4163:2020."
  },
  'logistics-cmr-sent-inspector': {
    field1LabelUk: "Номер CMR / e-ТТН & Перевізник",
    field1Value: "Міжнародна накладна CMR № 008412 / ТОВ 'Транс-Євро-Логістик' (Ліцензія ЄКМТ № 482)",
    field2LabelUk: "Реєстрація в польській системі SENT (PUESC)",
    field2Value: "Номер SENT: SENT_2026_08_48190 / Товар чутливої групи: Олія соняшникова (CN 1512)",
    field3LabelUk: "Маршрут & Пункт пропуску (єЧерга)",
    field3Value: "м. Полтава (UA) — м. Гданськ (PL) / ПП 'Краківець — Корчова' / Автопоїзд 22 тонни",
    field4LabelUk: "Базис поставки Incoterms 2020 & Страхування",
    field4Value: "DAP Гданськ (Incoterms 2020) / Страховий поліс CMR-страхування на суму 100 000 EUR",
    field5LabelUk: "GPS-трекер вантажу (вимога SENT)",
    field5Value: "Геолокатор PUESC увімкнено (ID пристрою: PL-GEO-9821-X, передача координат щохвилини)",
    field6LabelUk: "Вимоги до водія & Дозвільні документи (ZUS / A1)",
    field6Value: "Посвідчення ADR (за необхідності), Чіп-карта водія тахографа, Довідка форми A1 для відрядження в ЄС",
    notesText: "Провести повний юридично-логістичний аудит заповнення міжнародної автонакладної CMR та перевірити статус реєстрації рейсу в польській системі SENT на платформі PUESC. Перевірити наявність активного GPS-моніторингу транспорту для уникнення штрафу 20 000 PLN. Перевірити розподіл ризиків і витрат за правилом DAP Incoterms 2020 та згенерувати Пам'ятку водію й Звіт перевірки у Word (.docx)."
  },
  'logistics-customs-broker': {
    field1LabelUk: 'Найменування товару & Заводський опис', field1Value: 'Промисловий частотний перетворювач (інвертор) 75 кВт',
    field2LabelUk: 'Країна походження & Країна відправлення', field2Value: 'Німеччина (DE) / Німеччина',
    field3LabelUk: 'Фактурна вартість & Валюта контрактів', field3Value: '18 500 EUR (FCA Мюнхен)',
    field4LabelUk: 'Вага нетто / брутто (кг) & Кількість місць', field4Value: '145 кг нетто / 168 кг брутто (1 дерев\'яний палет)',
    field5LabelUk: 'Режим митного оформлення', field5Value: 'Імпорт (ІМ-40 АА - Випуск у вільний обіг)',
    field6LabelUk: 'Запропонований код УКТ ЗЕД (якщо є)', field6Value: '8504 40 88 00 (Ставка мита 0%, ПДВ 20%)',
    notesText: 'Провести класифікацію обладнання за кодом УКТ ЗЕД, розрахувати ввізне мито, митний збір та ПДВ 20%, перевірити вимоги щодо наявності Декларації про відповідність технічним регламентам (Низьковольтне обладнання & ЕМС) та сформувати проєкт митної декларації.'
  },
  'logistics-multimodal-dispatcher': {
    field1LabelUk: 'Тип вантажу & Вага/Об\'єм', field1Value: 'Соняшникові шроти в біг-бегах / 44 тонни (2 автопоїзди)',
    field2LabelUk: 'Пункт відправлення & Пункт призначення', field2Value: 'м. Дніпро (UA) — Порт Констанца (RO)',
    field3LabelUk: 'Обраний пункт пропуску через ДПС («єЧерга»)', field3Value: 'ПП «Порубне — Сирет» (Чернівецька область)',
    field4LabelUk: 'Базовий тариф фрахту ($/т або €/рейс)', field4Value: '2 800 EUR / автопоїзд 22 тонни',
    field5LabelUk: 'Поточний статус у системі «єЧерга»', field5Value: 'Очікуваний час заїзду в пункт пропуску: 18 годин',
    field6LabelUk: 'Вагогабаритні параметри (ГВК) & Осі', field6Value: 'Навантаження на вісь: 9.2 т (в межах норми 11.5 т)',
    notesText: 'Оптимізувати графік подачі автопоїздів під час проходження «єЧерги», розрахувати загальну вартість мультимодального фрахту Дніпро-Констанца з урахуванням перевалки у порту та сформувати транспортну накладну CMR.'
  },
  'logistics-tco-kik-auditor': {
    field1LabelUk: 'Назва пов\'язаної особи & Резиденція', field1Value: 'Trading Company AG (Швейцарія / Кантон Цуг)',
    field2LabelUk: 'Вид контрольованої операції за ст. 39 ПКУ', field2Value: 'Експорт зернових (Кукурудза) пов\'язаній особі',
    field3LabelUk: 'Річний обсяг контрольованих операцій (грн)', field3Value: '145 000 000 грн / рік (перевищує поріг 10 млн грн)',
    field4LabelUk: 'Річний дохід української компанії (грн)', field4Value: '480 000 000 грн (перевищує поріг 150 млн грн)',
    field5LabelUk: 'Метод ТЦО для обґрунтування ціни', field5Value: 'Метод порівняльної неконтрольованої ціни (CUP / Біржові котування)',
    field6LabelUk: 'Статус КІК (Контрольованої Іноземної Компанії)', field6Value: 'Підгалузева КІК (Частка володіння 100%, Пасивні доходи 15%)',
    notesText: 'Провести аудит ТЦО контрольованих експортних операцій за 2025 рік, перевірити відповідність контрактних цін біржовим котируванням (Platts / Argus) за принципом «витягнутої руки», сформувати документацію з ТЦО (Local File) та підготувати звіт про КІК.'
  }
};

const LOADING_STEPS_UK = [
  "Ініціалізація мультиагентної команди Логістики та Митниці...",
  "Обробка митних кодів УКТ ЗЕД, тарифної номенклатури CN / TARIC та баз SENT...",
  "Розрахунок митних платежів, митної вартості, ПДВ та перевірка преференцій EUR.1...",
  "Аналіз умов Incoterms 2020, CMR та перевірка вимог GPS-моніторингу...",
  "Формування підсумкового Митно-Логістичного Звіту за ДСТУ 4163:2020..."
];

const LOADING_STEPS_PL = [
  "Inicjalizacja zespołu ekspertów celnych i logistycznych...",
  "Weryfikacja kodów taryfowych CN / TARIC, zgłoszeń SAD i bazy SENT...",
  "Kalkulacja należności celno-skarbowych, cła antydumpingowego i preferencji EUR.1...",
  "Audyt listu przewozowego CMR, reguł Incoterms 2020 i geolokalizacji PUESC...",
  "Generowanie oficjalnego raportu odprawy celno-spedycyjnej Word (.docx)..."
];

const LOADING_STEPS_EN = [
  "Initializing customs clearance and international logistics multi-agent crew...",
  "Cross-matching commodity codes (HS / CN / TARIC) and verifying SAD / CMR documents...",
  "Calculating customs duty, import VAT, trade tariffs, and EUR.1 preferences...",
  "Auditing Incoterms 2020 obligations, freight documentation, and SENT compliance...",
  "Generating final customs validation report and logistics checklist in Word (.docx)..."
];

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <Truck className={className} />;
  return <IconComponent className={className} />;
};

export default function LogisticsAgentView({
  agentId,
  language,
  onBack,
  onGoHome,
  useEnterpriseContext,
  enterpriseName
}: LogisticsAgentViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';

  const agentConfig = agentsConfig.find((a: any) => a.id === agentId) || {
    nameUk: 'Логістичний & Митний Аналітичний Модуль',
    nameEn: 'Logistics & Customs AI Agent',
    namePl: 'Moduł Celno-Logistyczny AI',
    descriptionUk: 'Аналіз митниці, декларацій МД-2/SAD, CMR, SENT та ЗЕД.',
    descriptionEn: 'Customs, SAD/MD-2 declarations, CMR, SENT, and foreign trade analytics.',
    descriptionPl: 'Audyt celny, deklaracji SAD/MD-2, listów CMR, SENT i Incoterms.',
    icon: 'Truck',
    categoryUk: 'ЛОГІСТИКА & ЗЕД',
    categoryEn: 'LOGISTICS & CUSTOMS',
    categoryPl: 'LOGISTYKA & CŁO'
  };

  const defaultDemo = DEFAULT_LOGISTICS_DEMO[agentId] || DEFAULT_LOGISTICS_DEMO['customs-sad-md2-declarator'] || DEFAULT_LOGISTICS_DEMO['logistics-customs-broker'];

  const [field1, setField1] = useState(getFieldValue(defaultDemo, 1, language));
  const [field2, setField2] = useState(getFieldValue(defaultDemo, 2, language));
  const [field3, setField3] = useState(getFieldValue(defaultDemo, 3, language));
  const [field4, setField4] = useState(getFieldValue(defaultDemo, 4, language));
  const [field5, setField5] = useState(getFieldValue(defaultDemo, 5, language));
  const [field6, setField6] = useState(getFieldValue(defaultDemo, 6, language));
  const [notesText, setNotesText] = useState(getNotesText(defaultDemo, language));

  const [attachedFiles, setAttachedFiles] = useState<Array<{ name: string; size: number; text: string }>>([]);
  const [isReadingFile, setIsReadingFile] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const [reportResult, setReportResult] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);

  useEffect(() => {
    setField1(getFieldValue(defaultDemo, 1, language));
    setField2(getFieldValue(defaultDemo, 2, language));
    setField3(getFieldValue(defaultDemo, 3, language));
    setField4(getFieldValue(defaultDemo, 4, language));
    setField5(getFieldValue(defaultDemo, 5, language));
    setField6(getFieldValue(defaultDemo, 6, language));
    setNotesText(getNotesText(defaultDemo, language));
    setReportResult(null);
  }, [defaultDemo, language]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isProcessing) {
      setLoadingStepIndex(0);
      const steps = isPl ? LOADING_STEPS_PL : isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN;
      interval = setInterval(() => {
        setLoadingStepIndex(prev => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing, isUk, isPl]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsReadingFile(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch('/api/extract-text', { method: 'POST', body: formData });

        if (res.ok) {
          const data = await res.json();
          setAttachedFiles(prev => [...prev, { name: file.name, size: file.size, text: data.text || '' }]);
        } else {
          setAttachedFiles(prev => [...prev, { name: file.name, size: file.size, text: `[Файл ${file.name} додано]` }]);
        }
      }
    } catch (err) {
      console.error('File upload error:', err);
    } finally {
      setIsReadingFile(false);
    }
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRunAnalysis = async () => {
    setIsProcessing(true);
    setReportResult(null);
    setErrorMessage(null);
    setLoadingStepIndex(0);

    try {
      const combinedDocText = attachedFiles.map(f => `--- ФАЙЛ: ${f.name} ---\n${f.text}`).join('\n\n');
      
      const inputPrompt = `
[ЗАВДАННЯ / ДЕКЛАРАЦІЯ / ТРАНСПОРТНІ ДОКУМЕНТИ]:
${notesText}

[ВХІДНІ ДАНІ МИТНИЦІ ТА ЛОГІСТИКИ]:
- ${defaultDemo.field1LabelUk}: ${field1}
- ${defaultDemo.field2LabelUk}: ${field2}
- ${defaultDemo.field3LabelUk}: ${field3}
- ${defaultDemo.field4LabelUk}: ${field4}
- ${defaultDemo.field5LabelUk}: ${field5}
- ${defaultDemo.field6LabelUk}: ${field6}

${combinedDocText ? `[ДОДАНІ ІНВОЙСИ / CMR / ДЕКЛАРАЦІЇ SAD / МД-2]:\n${combinedDocText}` : ''}
`;

      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const response = await fetch('/api/run-templated-crew', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          crewName: agentConfig.nameUk || "Митний Аудитор & Валідатор Декларацій МД-2 / SAD (УКТ ЗЕД ↔ CN / TARIC)",
          agentId: agentId,
          inputs: {
            task_description: inputPrompt,
            agent_name: agentConfig.nameUk,
            target_language: isPl ? 'pl' : isUk ? 'uk' : 'en'
          },
          useEnterpriseContext
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isPl ? 'Błąd wykonania audytu celno-logistycznego.' : isUk ? 'Помилка виконання митно-логістичного аналізу.' : 'Execution failed.'));
      }

      const data = await response.json();

      if (data.jobId) {
        const pollJob = async () => {
          try {
            const statusRes = await fetch(`/api/crew-status/${data.jobId}`);
            if (!statusRes.ok) {
              throw new Error(isPl ? 'Błąd sprawdzania statusu zadania.' : isUk ? 'Помилка перевірки статусу завдання.' : 'Status check failed.');
            }
            const statusData = await statusRes.json();
            if (statusData.status === 'completed') {
              const rawReport = statusData.report || statusData.result || '';
              const resText = typeof rawReport === 'string'
                ? rawReport
                : (typeof rawReport === 'object' && rawReport !== null
                    ? (rawReport.report || rawReport.result || JSON.stringify(rawReport, null, 2))
                    : String(rawReport));

              if (!resText) {
                throw new Error(isPl ? 'Serwer zwrócił pusty raport.' : isUk ? 'Сервер повернув порожній результат звіту.' : 'Server returned empty report.');
              }
              setReportResult(resText);
              setIsProcessing(false);
            } else if (statusData.status === 'failed') {
              setErrorMessage(statusData.error || (isPl ? 'Błąd generowania raportu.' : isUk ? 'Помилка генерації звіту.' : 'Report generation failed.'));
              setIsProcessing(false);
            } else {
              setTimeout(pollJob, 2500);
            }
          } catch (pollErr: any) {
            console.error('Polling error:', pollErr);
            setErrorMessage(pollErr.message || (isPl ? 'Wystąpił błąd podczas pobierania wyników.' : isUk ? 'Сталася помилка при отриманні результатів.' : 'Polling error occurred.'));
            setIsProcessing(false);
          }
        };
        pollJob();
      } else if (data.report) {
        setReportResult(data.report);
        setIsProcessing(false);
      } else {
        throw new Error(isPl ? 'Nieoczekiwana odpowiedź serwera.' : isUk ? 'Неочікувана відповідь сервера.' : 'Unexpected server response.');
      }
    } catch (err: any) {
      console.error(err);
      setErrorMessage(err.message || (isPl ? 'Wystąpił błąd podczas analizy.' : isUk ? 'Сталася помилка під час аналізу.' : 'An error occurred.'));
      setIsProcessing(false);
    }
  };

  const handleDownloadWord = async () => {
    if (!reportResult) return;
    try {
      const name = (agentConfig.nameUk || "Logistics_Customs_Report").replace(/[^a-zA-Z0-9А-Яа-яЄєІіЇїҐґ_-]/g, "_");
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: reportResult, fileName: name })
      });
      if (!response.ok) throw new Error(isPl ? 'Nie udało się pobrać Word pliku.' : isUk ? 'Не вдалося згенерувати Word файл.' : 'Failed to generate Word.');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.message);
    }
  };

  const applyPresetScenario = (scenario: 'ua' | 'pl' | 'eu') => {
    if (scenario === 'ua') {
      setField1("ВМД форма МД-2 (ІМ-40 ДЕ) / Фасувальна лінія для зерна");
      setField2("8422 30 00 00 (УКТ ЗЕД) ↔ 8422 30 00 90 (TARIC EU) — Ставка мита 0%, ПДВ 20%");
      setField3("Польща (PL) / 64 500 EUR (DAP Київ) / Митна вартість: 2 890 000 грн");
      setField4("Інвойс № PL-4819, Сертифікат EUR.1 № PL/2026/004812, Пакувальний лист");
      setField5("Ввізне мито 0% (преференція Угода про асоціацію), ПДВ 20%: 578 000 грн");
      setField6("Декларація про відповідність ТР Безпеки машин (ПКМУ № 62)");
      setNotesText("Здійснити комплексну валідацію проекту митної декларації МД-2 / SAD. Звірити 10-значний код УКТ ЗЕД із товарною номенклатурою ЄС (TARIC). Перевірити повноту та коректність заявленої митної вартості за методом 1 (ціна контракту), застосування преференційного тарифу за сертифікатом EUR.1, відповідність інвойсів пакувальним листам та сформувати офіційний Чек-лист митного комплаєнсу у Word (.docx) за ДСТУ 4163:2020.");
    } else if (scenario === 'pl') {
      setField1("Zgłoszenie celne SAD (Procedura 40 00) / PUESC / Import paneli fotowoltaicznych z UA do PL");
      setField2("Kod CN 8541 43 00 (Moduły fotowoltaiczne) — Cło 0%, Podatek VAT 23%");
      setField3("Ukraina (UA) / 120 000 EUR (FCA Lwów) / Wartość celna w PLN: 522 000 PLN");
      setField4("Faktura handlowa VAT, Świadectwo pochodzenia EUR.1 nr UA/2026/9182, List przewozowy CMR");
      setField5("Kalkulacja: Cło 0 PLN, VAT 23% (art. 33a ustawy o VAT — rozliczenie w deklaracji JPK_V7)");
      setField6("Zgłoszenie w rejestrze SENT (Platforma PUESC) + numer referencyjny REX eksportera");
      setNotesText("Przeprowadzić pełny audyt zgłoszenia celnego SAD w procedurze dopuszczenia do obrotu w Polsce. Zweryfikować poprawność taryfikacji towaru (kod CN), zastosowanie bezcłowej stawki preferencyjnej z Ukrainą na podstawie świadectwa EUR.1 oraz rozliczenie podatku VAT z importu bezgotówkowo zgodnie z art. 33a ustawy o VAT. Sprawdzić ważność numeru EORI importera i przygotować raport odprawy.");
    } else if (scenario === 'eu') {
      setField1("EU Single Administrative Document (SAD Import T1 Transit) / Port of Rotterdam Clearance");
      setField2("TARIC Code 8708 29 90 00 (Automotive Components) — Third country duty 4.5%, VAT 21%");
      setField3("Japan (JP) to EU Distribution Center / Value: €245,000 (CIF Rotterdam)");
      setField4("Commercial Invoice, Bill of Lading (B/L), EU-Japan EPA Statement on Origin");
      setField5("Preferential Duty: 0% under EU-Japan Economic Partnership Agreement, Import VAT deferred");
      setField6("CE Marking, RoHS Directive compliance certificate, Authorized Economic Operator (AEO-F)");
      setNotesText("Audit EU SAD transit and customs clearance documentation at Port of Rotterdam. Verify AEO-F fast-track procedures, origin criteria under EU-Japan EPA, and automated import VAT deferment. Generate comprehensive Customs Compliance Audit Statement and Clearance Action Plan in Word (.docx).");
    }
  };

  return (
    <div className="space-y-8 animate-fade-in pb-16 font-mono max-w-5xl mx-auto px-4">
      {/* Top Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <div className="flex items-center gap-3">
          <button
            onClick={onGoHome || onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
          >
            🏠 {isPl ? 'Strona Główna' : isUk ? 'На головну' : 'Home'}
          </button>
          <button
            onClick={onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
          >
            <ArrowLeft className="w-4 h-4 text-sky-400" />
            <span>{isPl ? '← Powrót do Logistyki' : isUk ? '← До Центру Логістики' : '← Back to Logistics Hub'}</span>
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-sky-500/20 text-sky-300 border border-sky-400/30 rounded-md flex items-center gap-1">
            <Globe className="w-3.5 h-3.5 text-sky-400" />
            <span>UA • PL • EU CUSTOMS & LOGISTICS</span>
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-md">
            МД-2 • SAD • SENT • CMR • INCOTERMS 2020
          </span>
        </div>
      </div>

      {useEnterpriseContext && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3.5 flex items-center justify-between text-xs text-amber-800">
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-amber-600 animate-pulse" />
            <div>
              <span className="font-bold">{isPl ? "Aktywny kontekst firmy: " : isUk ? "Активовано контекст підприємства: " : "Enterprise Context Active: "}</span>
              <span className="font-mono">{enterpriseName || (isPl ? "Baza wiedzy przedsiębiorstwa" : isUk ? "База знань компанії" : "Company Knowledge Base")}</span>
            </div>
          </div>
          <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 rounded text-amber-700 font-bold uppercase tracking-wider">
            {isPl ? "Corporate DNA" : isUk ? "База знань" : "Knowledge Base"}
          </span>
        </div>
      )}

      {/* Hero Banner */}
      <div className="bg-slate-950 border border-sky-500/40 rounded-2xl p-6 sm:p-8 text-white shadow-2xl space-y-4">
        <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
          <div className="p-4 bg-sky-950/80 border border-sky-500/40 rounded-2xl text-sky-400 shrink-0 shadow-lg">
            <AgentIcon name={agentConfig.icon} className="w-8 h-8" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-sky-500/30 text-sky-200 border border-sky-400/40 rounded">
                {getLocalizedAgentCategory(agentConfig, language)}
              </span>
              <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-200 border border-emerald-400/40 rounded flex items-center gap-1">
                <ShieldCheck className="w-3 h-3 text-emerald-400" />
                <span>100% Митний & Логістичний Комплаєнс</span>
              </span>
            </div>
            <h1 className="text-xl sm:text-2xl font-extrabold uppercase tracking-wide text-white">
              {getLocalizedAgentName(agentConfig, language)}
            </h1>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-mono">
              {getLocalizedAgentDesc(agentConfig, language)}
            </p>
          </div>
        </div>
      </div>

      {/* 1-Click Fast Presets */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-white space-y-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-extrabold uppercase tracking-wide flex items-center gap-1.5 text-amber-400">
            <Sparkles className="w-4 h-4 text-amber-400 animate-pulse" />
            <span>{isPl ? "⚡ Gotowe scenariusze celno-logistyczne (1 kliknięcie):" : isUk ? "⚡ Готові сценарії митниці та логістики (1 клік):" : "⚡ Ready Customs & Logistics Scenarios (1-Click Fill):"}</span>
          </span>
          <span className="text-[10px] text-slate-400 font-mono">МД-2 • SAD • SENT • CMR • EUR.1</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => applyPresetScenario('ua')}
            className="p-3 bg-slate-800 hover:bg-slate-750 border border-slate-700 hover:border-emerald-500/50 rounded-xl text-left transition-all cursor-pointer group"
          >
            <div className="text-xs font-bold text-emerald-300 group-hover:text-emerald-200 flex items-center gap-1.5">
              <span>🇺🇦 Декларація МД-2 (Україна)</span>
            </div>
            <div className="text-[10px] text-slate-400 mt-1 font-mono">ІМ-40 • УКТ ЗЕД • EUR.1 • ПДВ 20%</div>
          </button>

          <button
            type="button"
            onClick={() => applyPresetScenario('pl')}
            className="p-3 bg-slate-800 hover:bg-slate-750 border border-slate-700 hover:border-blue-500/50 rounded-xl text-left transition-all cursor-pointer group"
          >
            <div className="text-xs font-bold text-blue-300 group-hover:text-blue-200 flex items-center gap-1.5">
              <span>🇵🇱 Zgłoszenie SAD & SENT (Polska)</span>
            </div>
            <div className="text-[10px] text-slate-400 mt-1 font-mono">PUESC • SENT • CN/TARIC • Art. 33a VAT</div>
          </button>

          <button
            type="button"
            onClick={() => applyPresetScenario('eu')}
            className="p-3 bg-slate-800 hover:bg-slate-750 border border-slate-700 hover:border-purple-500/50 rounded-xl text-left transition-all cursor-pointer group"
          >
            <div className="text-xs font-bold text-purple-300 group-hover:text-purple-200 flex items-center gap-1.5">
              <span>🇪🇺 EU Transit & SAD (Rotterdam / EU)</span>
            </div>
            <div className="text-[10px] text-slate-400 mt-1 font-mono">T1 Transit • EPA Origin • AEO-F</div>
          </button>
        </div>
      </div>

      {/* Main Parameters Form */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 space-y-6 text-white shadow-xl">
        <h3 className="text-sm font-extrabold uppercase tracking-wider text-sky-300 flex items-center gap-2 border-b border-slate-800 pb-3">
          <Truck className="w-4 h-4 text-sky-400" />
          <span>{isPl ? 'PARAMETRY ODPRAWY CELNEJ I LISTU PRZEWOZOWEGO:' : isUk ? 'ВХІДНІ ПАРАМЕТРИ МИТНОГО ОФОРМЛЕННЯ ТА ПЕРЕВЕЗЕННЯ:' : 'CUSTOMS & LOGISTICS PARAMETERS:'}</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">1. {getFieldLabel(defaultDemo, 1, language)}</label>
            <input
              type="text"
              value={field1}
              onChange={e => setField1(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-sky-500 focus:outline-none font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">2. {getFieldLabel(defaultDemo, 2, language)}</label>
            <input
              type="text"
              value={field2}
              onChange={e => setField2(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-sky-500 focus:outline-none font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">3. {getFieldLabel(defaultDemo, 3, language)}</label>
            <input
              type="text"
              value={field3}
              onChange={e => setField3(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-sky-500 focus:outline-none font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">4. {getFieldLabel(defaultDemo, 4, language)}</label>
            <input
              type="text"
              value={field4}
              onChange={e => setField4(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-sky-500 focus:outline-none font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">5. {getFieldLabel(defaultDemo, 5, language)}</label>
            <input
              type="text"
              value={field5}
              onChange={e => setField5(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-sky-500 focus:outline-none font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">6. {getFieldLabel(defaultDemo, 6, language)}</label>
            <input
              type="text"
              value={field6}
              onChange={e => setField6(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-sky-500 focus:outline-none font-mono"
            />
          </div>
        </div>

        <div className="space-y-2 pt-2">
          <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">
            {isPl ? '7. ZAKRES WERYFIKACJI I WYMAGANIA ODPRAWY CELNO-SPEDYCYJNEJ:' : isUk ? '7. ДЕТАЛЬНИЙ ОПИС ВАНТАЖУ, ІНВОЙСУ ТА ЗАВДАННЯ АУДИТУ:' : '7. SHIPMENT DETAILS & CLEARANCE AUDIT INSTRUCTIONS:'}
          </label>
          <textarea 
            rows={5} 
            value={notesText} 
            onChange={e => setNotesText(e.target.value)} 
            className="w-full p-3.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-sky-500 focus:outline-none font-mono leading-relaxed" 
          />
        </div>

        {/* File Attachments */}
        <div className="space-y-3 pt-4 border-t border-slate-800">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-300 uppercase tracking-wide flex items-center gap-2">
              <Upload className="w-4 h-4 text-sky-400" />
              <span>{isPl ? 'ZAŁĄCZNIKI (FAKTURY / SAD / CMR / EUR.1 / PDF / XLSX):' : isUk ? 'СУПРОВІДНІ ФАЙЛИ ІНВОЙСІВ, ДЕКЛАРАЦІЙ SAD, CMR ТА EUR.1 (PDF / DOCX / XLSX):' : 'SUPPORTING INVOICES, SAD DECLARATIONS, CMR & EUR.1 FILES:'}</span>
            </span>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <label className="px-4 py-2.5 bg-sky-950/80 hover:bg-sky-900 border border-sky-500/40 text-sky-200 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-2">
              <Upload className="w-4 h-4 text-sky-400" />
              <span>{isReadingFile ? (isPl ? 'Odczytywanie...' : 'Зчитування...') : (isPl ? '➕ Dodaj dokumenty celne' : '➕ Завантажити документи')}</span>
              <input type="file" multiple accept=".pdf,.docx,.xlsx,.csv,.json,.txt" onChange={handleFileUpload} className="hidden" />
            </label>

            {attachedFiles.map((file, idx) => (
              <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-slate-950 border border-slate-700 rounded-lg text-xs text-slate-200">
                <FileCheck className="w-3.5 h-3.5 text-sky-400" />
                <span className="font-mono text-[11px] max-w-[150px] truncate">{file.name}</span>
                <button onClick={() => removeFile(idx)} className="text-slate-400 hover:text-red-400 p-0.5">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {errorMessage && (
          <div className="p-3.5 bg-red-500/10 border border-red-500/30 rounded-xl text-xs text-red-300 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Action Button */}
        <div className="pt-4 border-t border-slate-800 flex justify-end">
          <button
            onClick={handleRunAnalysis}
            disabled={isProcessing || !notesText.trim()}
            className="w-full sm:w-auto px-8 py-3.5 bg-gradient-to-r from-sky-600 via-indigo-600 to-sky-700 hover:from-sky-500 hover:to-indigo-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 hover:scale-[1.01]"
          >
            {isProcessing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin text-white" />
                <span>{isPl ? LOADING_STEPS_PL[loadingStepIndex] : isUk ? LOADING_STEPS_UK[loadingStepIndex] : LOADING_STEPS_EN[loadingStepIndex]}</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 text-amber-300" />
                <span>{isPl ? '🚀 ROZPOCZNIJ AUDYT CELNO-LOGISTYCZNY' : isUk ? '🚀 ЗАПУСТИТИ МИТНО-ЛОГІСТИЧНИЙ АУДИТ' : '🚀 RUN CUSTOMS & LOGISTICS AUDIT'}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Result Section */}
      {reportResult && (
        <div className="bg-slate-950 border border-sky-500/40 rounded-2xl p-6 sm:p-8 space-y-4 text-white shadow-2xl animate-fade-in">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3 flex-wrap gap-2">
            <h3 className="text-sm font-extrabold uppercase tracking-wide text-sky-300 flex items-center gap-2">
              <FileText className="w-4 h-4 text-sky-400" />
              <span>{isPl ? 'OFICJALNY PROTOKÓŁ ODPRAWY CELNEJ I LOGISTYKI:' : isUk ? 'ОФІЦІЙНИЙ МИТНО-ЛОГІСТИЧНИЙ ВИСНОВОК & ПРОТОКОЛ ВАЛІДАЦІЇ:' : 'OFFICIAL CUSTOMS & FREIGHT AUDIT FINDINGS:'}</span>
            </h3>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleDownloadWord}
                className="px-3 py-1.5 bg-sky-950 hover:bg-sky-900 border border-sky-500/40 text-sky-200 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <Download className="w-3.5 h-3.5 text-sky-400" />
                <span>Word (.docx)</span>
              </button>

              <ExcelExportButton
                reportText={reportResult}
                fileName={`${agentConfig.nameUk || 'Logistics_Customs'}_data`}
                className="px-3 py-1.5 bg-emerald-950 hover:bg-emerald-900 border border-emerald-500/40 text-emerald-200 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
              />

              <TelegramExportButton
                reportText={reportResult}
                agentName={getLocalizedAgentName(agentConfig, language)}
              />

              <button
                onClick={() => setShowSignModal(true)}
                className="px-3 py-1.5 bg-amber-950 hover:bg-amber-900 border border-amber-500/40 text-amber-200 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <ShieldCheck className="w-3.5 h-3.5 text-amber-400" />
                <span>{isPl ? '✍️ Podpisz QES' : isUk ? '✍️ Підписати КЕП' : '✍️ Sign QES'}</span>
              </button>

              <button
                onClick={() => {
                  navigator.clipboard.writeText(reportResult);
                  setIsCopied(true);
                  setTimeout(() => setIsCopied(false), 2000);
                }}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                {isCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5 text-slate-300" />}
                <span>{isCopied ? (isPl ? 'Skopiowano!' : isUk ? 'Скопійовано!' : 'Copied!') : (isPl ? 'Kopiuj' : isUk ? 'Копіювати' : 'Copy')}</span>
              </button>
            </div>
          </div>

          <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-5 text-xs font-mono leading-relaxed text-slate-200 whitespace-pre-wrap max-h-[650px] overflow-y-auto">
            {reportResult}
          </div>
        </div>
      )}

      {showSignModal && reportResult && (
        <DigitalSignatureModal
          isOpen={showSignModal}
          onClose={() => setShowSignModal(false)}
          documentText={reportResult}
          documentTitle={getLocalizedAgentName(agentConfig, language)}
          language={language}
        />
      )}
    </div>
  );
}
